import {
  ArchiveOutlined,
  CheckCircleOutlined,
  EditOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
} from '@mui/icons-material'
import {
  Chip,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useMemo } from 'react'

import TestBadgeChip from '~/features/campaigns/components/testBadgeChip'
import { ECampaignExecutionStatus } from '~/features/campaigns/models'
import { getExecutionStatusBadgeSettings } from '~/features/campaigns/utils/executionStatusBadge'
import useGetSelectedCampaign from '~/hooks/useGetSelectedCampaign'
import rem from '~/utils/ui'

export default function OfuManageCampaignStatus() {
  const theme = useTheme()
  const isSmallViewport = useMediaQuery(theme.breakpoints.down('md'))

  const { isLoading, status, isArchived, isTest } = useGetSelectedCampaign()

  const statusBadgeSetting = useMemo(
    () => getExecutionStatusBadgeSettings(theme, status, isArchived),
    [theme, status, isArchived],
  )

  const statusIcon = useMemo(() => {
    if (isArchived) {
      return ArchiveOutlined
    }

    switch (status) {
      case ECampaignExecutionStatus.notStarted:
        return EditOutlined
      case ECampaignExecutionStatus.paused:
        return PauseCircleOutlined
      case ECampaignExecutionStatus.completed:
        return CheckCircleOutlined
      case ECampaignExecutionStatus.inProgress:
      default:
        return PlayCircleOutlined
    }
  }, [isArchived, status])
  const StatusIcon = statusIcon

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {isLoading && (
        <>
          {!isSmallViewport && (
            <Skeleton variant="text" width={rem(100)} height={rem(22)} />
          )}
          <Skeleton
            variant={isSmallViewport ? 'circular' : 'rounded'}
            width={isSmallViewport ? rem(18) : rem(60)}
            height={isSmallViewport ? rem(18) : rem(22)}
          />
        </>
      )}

      {!isLoading && (
        <>
          {!isSmallViewport && (
            <Typography
              variant="body2"
              fontSize={rem(14)}
              fontWeight={400}
              color={theme.palette.text.secondary}
            >
              Status:
            </Typography>
          )}
          {isSmallViewport ? (
            <Tooltip title={statusBadgeSetting.label} placement="bottom">
              <Stack
                sx={{
                  width: rem(22),
                  height: rem(22),
                  borderRadius: '50%',
                  border: `${rem(1)} solid ${statusBadgeSetting.borderColor}`,
                  backgroundColor: statusBadgeSetting.backgroundColor,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <StatusIcon
                  sx={{
                    fontSize: rem(14),
                    color: statusBadgeSetting.color,
                  }}
                />
              </Stack>
            </Tooltip>
          ) : (
            <Chip
              label={statusBadgeSetting.label}
              color="primary"
              size="small"
              sx={{
                borderRadius: rem(6),
                border: `${rem(1)} solid ${statusBadgeSetting.borderColor}`,
                backgroundColor: statusBadgeSetting.backgroundColor,
                color: statusBadgeSetting.color,
                height: rem(22),
                textAlign: 'center',
              }}
            />
          )}
          <TestBadgeChip
            isTest={isTest}
            statusLabel={statusBadgeSetting.label}
          />
        </>
      )}
    </Stack>
  )
}
